import React, { createContext, useMemo, useState, useCallback, useEffect } from 'react';
import { oauthService } from '../../services/oauthService';
import { ROLE_ADMIN, ROLE_USER } from '../roles';

const ROLE_STORAGE_KEY = 'scc_active_role';
const ROLE_SELECTED_KEY = 'scc_role_selected';

/**
 * Priority order for roles — highest priority first.
 * Used when determining the default active role.
 */
const ROLE_PRIORITY = [ROLE_ADMIN, ROLE_USER];

export interface RbacContextValue {
  /** Currently active role claim value */
  activeRole: string;
  /** All recognised roles the user has (from Okta token) */
  availableRoles: string[];
  /** Whether the user can switch between multiple roles */
  canSwitchRoles: boolean;
  /** Whether the user has explicitly selected a role (for multi-role users) */
  roleSelected: boolean;
  /** Switch the active role (must be one of availableRoles) */
  switchRole: (role: string) => void;
  /** Clear role selection — returns user to the role selector page */
  clearRoleSelection: () => void;
  /** Raw role values from the Okta token (including unrecognised ones) */
  oktaRoles: string[];
  /** Whether the active role is admin */
  isAdmin: boolean;
  /** Whether the active role is a regular user */
  isUser: boolean;
  /** Whether the user has no recognised role (read-only fallback) */
  isReadOnly: boolean;
}

const defaultValue: RbacContextValue = {
  activeRole: '',
  availableRoles: [],
  canSwitchRoles: false,
  roleSelected: false,
  switchRole: () => {},
  clearRoleSelection: () => {},
  oktaRoles: [],
  isAdmin: false,
  isUser: false,
  isReadOnly: true,
};

export const RbacContext = createContext<RbacContextValue>(defaultValue);

interface RbacProviderProps {
  children: React.ReactNode;
}

export const RbacProvider: React.FC<RbacProviderProps> = ({ children }) => {
  // Get all role claim values from the JWT via oauthService
  const allOktaRoles = useMemo(() => oauthService.getUserRoles(), []);

  // Filter to only recognised roles, sorted by priority
  const availableRoles = useMemo(
    () => ROLE_PRIORITY.filter((r) => allOktaRoles.includes(r)),
    [allOktaRoles],
  );

  // Default role = highest priority recognised role
  const defaultRole = availableRoles[0] ?? '';

  // Initialise active role from sessionStorage if still valid
  const [activeRole, setActiveRole] = useState<string>(() => {
    const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
    if (stored && availableRoles.includes(stored)) {
      return stored;
    }
    return defaultRole;
  });

  // Track whether user has explicitly selected a role.
  // Single-role (or zero-role) users are auto-selected. Multi-role users must pick.
  const [roleSelected, setRoleSelected] = useState<boolean>(() => {
    if (availableRoles.length <= 1) { return true; }
    return sessionStorage.getItem(ROLE_SELECTED_KEY) === 'true';
  });

  // Keep activeRole in sync when availableRoles change (e.g. token refresh)
  useEffect(() => {
    if (activeRole && !availableRoles.includes(activeRole)) {
      setActiveRole(defaultRole);
      sessionStorage.setItem(ROLE_STORAGE_KEY, defaultRole);
    }
    // Auto-select for single-role users
    if (availableRoles.length <= 1) {
      setRoleSelected(true);
    }
  }, [availableRoles, activeRole, defaultRole]);

  const switchRole = useCallback(
    (role: string) => {
      if (availableRoles.includes(role)) {
        setActiveRole(role);
        setRoleSelected(true);
        sessionStorage.setItem(ROLE_STORAGE_KEY, role);
        sessionStorage.setItem(ROLE_SELECTED_KEY, 'true');
      }
    },
    [availableRoles],
  );

  const clearRoleSelection = useCallback(() => {
    setRoleSelected(false);
    sessionStorage.removeItem(ROLE_SELECTED_KEY);
  }, []);

  const value = useMemo<RbacContextValue>(() => {
    return {
      activeRole,
      availableRoles,
      canSwitchRoles: availableRoles.length > 1,
      roleSelected,
      switchRole,
      clearRoleSelection,
      oktaRoles: allOktaRoles,
      isAdmin: activeRole === ROLE_ADMIN,
      isUser: activeRole === ROLE_USER,
      isReadOnly: activeRole !== ROLE_ADMIN && activeRole !== ROLE_USER,
    };
  }, [activeRole, availableRoles, roleSelected, switchRole, clearRoleSelection, allOktaRoles]);

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};
